import { useState, useEffect } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Icon,
  useColorModeValue,
  Spinner,
  Avatar,
  Button,
} from '@chakra-ui/react';
import { Trophy, Sparkles, ChevronDown, ChevronRight } from 'lucide-react';
import { collection, query, where, getDocs, orderBy, Timestamp, limit } from 'firebase/firestore';
import { db } from '../../../../utils/init-firebase';
import { COLLECTION_NAMES, WinExcited, User } from '../../types';

interface RecentWinsExcitedProps {
  isPresenter?: boolean;
  users?: User[];
}

export function RecentWinsExcited({ isPresenter = false, users = [] }: RecentWinsExcitedProps) {
  const [entries, setEntries] = useState<WinExcited[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedUserIds, setExpandedUserIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchRecentEntries();
  }, []);

  const fetchRecentEntries = async () => {
    try {
      // Get last meeting timestamp
      const notesQuery = query(
        collection(db, COLLECTION_NAMES.L10_NOTES),
        orderBy('meetingTimestamp', 'desc'),
        limit(1)
      );
      const notesSnapshot = await getDocs(notesQuery);

      let filterDate: Timestamp;
      if (!notesSnapshot.empty) {
        filterDate = notesSnapshot.docs[0].data().meetingTimestamp;
      } else {
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        filterDate = Timestamp.fromDate(sevenDaysAgo);
      }

      const winsQuery = query(
        collection(db, COLLECTION_NAMES.WINS_EXCITED),
        where('createdAt', '>=', filterDate),
        orderBy('createdAt', 'desc')
      );
      const winsSnapshot = await getDocs(winsQuery);
      const winsData = winsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as WinExcited[];

      setEntries(winsData);
    } catch (error) {
      console.error('Error fetching recent wins/excited:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleUserExpanded = (userId: string) => {
    setExpandedUserIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  if (loading) {
    return (
      <Box textAlign="center" p={6}>
        <Spinner />
        <Text mt={2}>Loading...</Text>
      </Box>
    );
  }

  if (entries.length === 0) {
    return (
      <Box bg={bgColor} borderWidth={1} borderColor={borderColor} borderRadius="md" p={12} textAlign="center">
        <HStack justify="center" spacing={3} mb={4}>
          <Icon as={Trophy} w={12} h={12} color="gray.300" />
          <Icon as={Sparkles} w={12} h={12} color="gray.300" />
        </HStack>
        <Text fontSize="xl" fontWeight="semibold" color="gray.700" mb={2}>
          No Wins or Excited Items Yet
        </Text>
        <Text color="gray.500" maxW="md" mx="auto">
          Start your meeting off right! Share what you've achieved this week and what you're looking forward to.
        </Text>
      </Box>
    );
  }

  const groupedByUser = entries.reduce((acc, entry) => {
    if (!acc[entry.userId]) {
      acc[entry.userId] = [];
    }
    acc[entry.userId].push(entry);
    return acc;
  }, {} as Record<string, WinExcited[]>);

  const totalCount = entries.length;
  const winsCount = entries.filter((e) => e.type === 'win').length;
  const excitedCount = entries.filter((e) => e.type === 'excited').length;

  return (
    <Box bg={bgColor} borderWidth={1} borderColor={borderColor} borderRadius="md" p={6}>
      <HStack justify="space-between" mb={6}>
        <HStack spacing={3}>
          <HStack spacing={2}>
            <Icon as={Trophy} w={6} h={6} color="yellow.600" />
            <Icon as={Sparkles} w={6} h={6} color="purple.600" />
          </HStack>
          <VStack align="start" spacing={0}>
            <Text fontSize="2xl" fontWeight="bold">
              This Week's Highlights
            </Text>
            <Text fontSize="sm" color="gray.500">
              What the team has accomplished and what's ahead
            </Text>
          </VStack>
        </HStack>
        <VStack align="end" spacing={0}>
          <Text fontSize="3xl" fontWeight="bold">
            {totalCount}
          </Text>
          <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide">
            Total
          </Text>
          <Text fontSize="sm" color="gray.600">
            <Text as="span" fontWeight="semibold" color="yellow.700">{winsCount}</Text> wins
          </Text>
          <Text fontSize="sm" color="gray.600">
            <Text as="span" fontWeight="semibold" color="purple.700">{excitedCount}</Text> excited
          </Text>
        </VStack>
      </HStack>

      <VStack spacing={6} align="stretch">
        {Object.entries(groupedByUser).map(([userId, userEntries]) => {
          const isExpanded = expandedUserIds.has(userId);
          const user = users.find((u) => u.id === userId || u.uid === userId);
          const userName = user?.name || `User ${userId}`;
          return (
            <Box key={userId}>
              <Button
                variant="ghost"
                onClick={() => isPresenter && toggleUserExpanded(userId)}
                isDisabled={!isPresenter}
                w="full"
                justifyContent="start"
                mb={3}
              >
                <Avatar size="sm" mr={2} />
                <VStack align="start" spacing={0} flex={1}>
                  <Text fontWeight="semibold">{userName}</Text>
                  <Text fontSize="xs" color="gray.500">
                    {userEntries.length} {userEntries.length === 1 ? 'entry' : 'entries'}
                  </Text>
                </VStack>
                {isPresenter && (
                  <Icon as={isExpanded ? ChevronDown : ChevronRight} w={5} h={5} color="gray.400" />
                )}
              </Button>
              {isExpanded && (
                <VStack spacing={2} align="stretch" ml={12}>
                  {userEntries.map((entry) => (
                    <Box
                      key={entry.id}
                      borderWidth={1}
                      borderColor={borderColor}
                      borderRadius="md"
                      p={3}
                    >
                      <HStack spacing={3}>
                        <Box
                          w={8}
                          h={8}
                          borderRadius="full"
                          bg={entry.type === 'win' ? 'yellow.50' : 'purple.50'}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Icon
                            as={entry.type === 'win' ? Trophy : Sparkles}
                            w={4}
                            h={4}
                            color={entry.type === 'win' ? 'yellow.600' : 'purple.600'}
                          />
                        </Box>
                        <VStack align="start" spacing={0} flex={1}>
                          <HStack spacing={2}>
                            <Text fontSize="sm" fontWeight="medium" textTransform="capitalize">
                              {entry.type}
                            </Text>
                          </HStack>
                          <Text fontSize="sm" color="gray.800" whiteSpace="pre-wrap">
                            {entry.content}
                          </Text>
                        </VStack>
                      </HStack>
                    </Box>
                  ))}
                </VStack>
              )}
            </Box>
          );
        })}
      </VStack>
    </Box>
  );
}

